import React, { useRef, useState, useEffect, useMemo } from "react";
import axios from "axios";
import ParivahanRCView from "../../Screens/Common/ParivahanRc";
import AuthorizedRCView from "../../Screens/Common/AuthorizedRC";
import SmartRCView from "../../Screens/Common/SmartRc";
import DigiLockerRCView from "../../Screens/Common/DigiLockerRC";
import PortalModal from "../PortalModal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import * as QRCodeLib from "qrcode";
import { stateCodesMap } from "../../utils/stateCodes";
import Front from "../../Assets/Logos/Front.png";
import Back from "../../Assets/Logos/Back.png";
import logo from "../../Assets/Logos/ashok1.png";

// ✅ CLOUDINARY DIRECT URLs
const CLOUDINARY_IMAGES = {
  chip: "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506720/CHIP-RC_zxcump.png",
  signatures: [
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506654/RC-IMG1_oenwfq.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506674/RC-IMG2_pvpyao.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1769506688/RC-IMG3_ruttdo.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1771504606/WhatsApp_Image_2026-02-19_at_5.35.36_PM-removebg-preview_soybor.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1771504616/WhatsApp_Image_2026-02-19_at_5.39.09_PM-removebg-preview_qd1fgf.png",
    "https://res.cloudinary.com/dfjeuiwro/image/upload/v1771504623/WhatsApp_Image_2026-02-19_at_5.41.25_PM-removebg-preview_dhye6c.png",
  ],
};

const getSignatureUrl = (downloadIndex) => {
  const sigIndex = downloadIndex % CLOUDINARY_IMAGES.signatures.length;
  return CLOUDINARY_IMAGES.signatures[sigIndex];
};

const getSignatureNumber = (downloadIndex) => {
  return (downloadIndex % CLOUDINARY_IMAGES.signatures.length) + 1;
};

// ✅ Helper: normalise apisUsed from either old string or new array format
// Backend now returns apisUsed[] — but handle legacy apiUsed string gracefully
const resolveApisUsed = (responseData) => {
  if (
    Array.isArray(responseData.apisUsed) &&
    responseData.apisUsed.length > 0
  ) {
    return responseData.apisUsed;
  }
  if (typeof responseData.apiUsed === "string" && responseData.apiUsed) {
    return [responseData.apiUsed];
  }
  return ["unknown"];
};

// ✅ Human-readable label for apisUsed array
const formatApisUsedLabel = (apisUsed = []) => {
  if (!apisUsed.length || apisUsed[0] === "unknown") return "UNKNOWN";
  const labelMap = {
    api1_kashi: "Kashi",
    api2_era_full: "ERA-Full",
    api3_era_v2: "ERA-V2",
    api4_idspay: "IDSPay",
  };
  return apisUsed.map((a) => labelMap[a] || a.toUpperCase()).join(" + ");
};

// ✅ CRITICAL HELPER: Safely convert any value to a renderable string
const safeString = (value, fieldName = "unknown") => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return String(value);
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === "object") {
    console.warn(
      `⚠️ [SAFE_STRING] Field "${fieldName}" is an OBJECT, converting to string:`,
      JSON.stringify(null),
    );
    if (value.name) return String(value.name);
    if (value.value) return String(value.value);
    if (value.label) return String(value.label);
    if (value.code) return String(value.code);
    if (value.text) return String(value.text);
    return "";
  }
  return String(value);
};

// ✅ CRITICAL: Normalize ALL fields in rcData to safe strings
const normalizeRcData = (data, vehicleNo) => {
  if (!data || typeof data !== "object") {
    console.error("❌ [NORMALIZE] rcData is null or not an object:", data);
    return data;
  }

  console.group(`🔍 [NORMALIZE] Scanning all fields for VRN: ${vehicleNo}`);
  console.log("📦 Raw API response data:", JSON.stringify(data, null, 2));

  const normalized = {};
  const problematicFields = [];

  Object.entries(data).forEach(([key, value]) => {
    if (
      value !== null &&
      value !== undefined &&
      typeof value === "object" &&
      !(value instanceof Date) &&
      !Array.isArray(value)
    ) {
      problematicFields.push({ key, value });
      console.error(
        `❌ [NORMALIZE] OBJECT FIELD FOUND: "${key}" =`,
        JSON.stringify(value),
      );
      normalized[key] = safeString(value, key);
    } else if (Array.isArray(value)) {
      console.warn(
        `⚠️ [NORMALIZE] ARRAY FIELD: "${key}" =`,
        JSON.stringify(value),
      );
      normalized[key] = value.map((v) => safeString(v, `${key}[]`)).join(", ");
    } else {
      normalized[key] = value;
    }
  });

  if (problematicFields.length === 0) {
    console.log("✅ [NORMALIZE] No problematic object fields found");
  } else {
    console.error(
      `❌ [NORMALIZE] Found ${problematicFields.length} object field(s) that were converted:`,
      problematicFields.map((f) => f.key),
    );
  }

  console.groupEnd();
  return normalized;
};

// ✅ CRITICAL: Normalize history item to safe strings for table rendering
const normalizeHistoryItem = (item, source = "unknown") => {
  const normalized = {
    id: safeString(item._id || item.id, "id"),
    service: safeString(item.service, "service"),
    vehicleNo: safeString(item.idNumber || item.vehicleNo || " ", "vehicleNo"),
    documentType: safeString(item.documentType || " ", "documentType"),
    date: item.createdAt || item.date || new Date(),
    status: safeString(item.status || "pending", "status"),
    amount:
      typeof item.amountDeducted === "number"
        ? item.amountDeducted
        : typeof item.amount === "number"
          ? item.amount
          : 0,
    name: safeString(item.owner_name || item.name || " ", "name"),
    details: safeString(item.maker_model || item.details || " ", "details"),
  };

  Object.entries(normalized).forEach(([key, val]) => {
    if (
      val !== null &&
      val !== undefined &&
      typeof val === "object" &&
      !(val instanceof Date)
    ) {
      console.error(
        `❌ [HISTORY_NORMALIZE] Still an object after normalization: "${key}" =`,
        val,
      );
      normalized[key] = JSON.stringify(val);
    }
  });

  console.log(
    `✅ [HISTORY_NORMALIZE] Normalized item from "${source}":`,
    normalized,
  );
  return normalized;
};

const getBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL("image/png");
      resolve(dataURL);
    };
    img.onerror = (error) => reject(error);
    img.src = url;
  });
};

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
pdfMake.vfs = pdfFonts.vfs;

export default function RegistrationCertificate() {
  const [vehicleNo, setVehicleNo] = useState("");
  const [selectedRC, setSelectedRC] = useState("smartrc");
  const [submitted, setSubmitted] = useState(true);
  const [showCard, setShowCard] = useState(false);
  const [rcHistory, setRcHistory] = useState([]);
  const [allRcHistory, setAllRcHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [error, setError] = useState("");
  const [rcPrices, setRcPrices] = useState({
    parivaanrc: 0,
    Authorized_Rc: 0,
    smartrc: 0,
    digilockerrc: 0,
  });
  const [rcAccess, setRcAccess] = useState({
    parivaanrc: false,
    Authorized_Rc: false,
    smartrc: false,
    digilockerrc: false,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });
  const itemsPerPage = 10;
  const [rcData, setRcData] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState({
    father_name: "",
    vehicle_chasi_number: "",
    vehicle_engine_number: "",
  });
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const printRef = useRef(null);
  const [isAccessLoading, setIsAccessLoading] = useState(true);
  const [showEditFields, setShowEditFields] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // ✅ UPDATED: store array of APIs that contributed, not just one string
  const [apisUsedForCurrentData, setApisUsedForCurrentData] = useState([]);

  const [includeSignature, setIncludeSignature] = useState(false);
  const [includeChip, setIncludeChip] = useState(false);
  const [downloadCount, setDownloadCount] = useState(0);

  useEffect(() => {
    const savedCount = localStorage.getItem("rcDownloadCount");
    if (savedCount) {
      setDownloadCount(parseInt(savedCount, 10));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("rcDownloadCount", downloadCount.toString());
  }, [downloadCount]);

  const fetchRcData = async () => {
    try {
      setIsAccessLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/access-check`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      const access = response.data?.serviceAccess || [];
      const prices = response.data?.servicePrices || {};
      const hasAuthorizedRc = access.includes("Authorized_Rc");
      const authorizedRcPrice = prices["Authorized_Rc"] || 0;
      setRcAccess({
        parivaanrc: access.includes("parivaanrc"),
        Authorized_Rc: hasAuthorizedRc,
        smartrc: access.includes("smartrc"),
        digilockerrc: access.includes("digilockerrc"),
      });
      setRcPrices({
        parivaanrc: prices.parivaanrc || 0,
        Authorized_Rc: authorizedRcPrice,
        smartrc: prices.smartrc || 0,
        digilockerrc: prices.digilockerrc || 0,
        pan: prices.pan || 0,
        rc_mobile: prices.rc_mobile || 0,
      });
    } catch (e) {
      console.error("❌ [FRONTEND] Error fetching access data:", e);
      toast.error("Failed to load service access information");
    } finally {
      setIsAccessLoading(false);
    }
  };

  useEffect(() => {
    fetchRcData();
  }, []);

  const fetchHistoryData = async () => {
    try {
      const endpoints = [
        "rc-parivaanrc",
        "rc-Authorized_Rc",
        "rc-smartrc",
        "rc-digilockerrc",
      ];
      const responses = await Promise.all(
        endpoints.map((endpoint) =>
          axios.get(
            `${process.env.REACT_APP_API_URL}/services/get-document-records?service=${endpoint}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          ),
        ),
      );

      let combined = responses.flatMap((res, idx) => {
        const data = res.data || [];
        return data.map((item) =>
          normalizeHistoryItem(
            { ...item, service: endpoints[idx] },
            endpoints[idx],
          ),
        );
      });

      const seen = new Set();
      const unique = combined.filter((item) => {
        const key = `${item.vehicleNo}-${item.date}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      unique.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAllRcHistory(unique);
      setRcHistory(unique);
    } catch (err) {
      console.error("❌ [FRONTEND] Error fetching RC history:", err);
      toast.error("Failed to fetch RC history");
    }
  };

  useEffect(() => {
    fetchHistoryData();
  }, []);

  // =========================================================================
  // VERIFY (preview — no charge)
  // =========================================================================
  const handleVerify = async () => {
    await fetchRcData();
    if (!vehicleNo || typeof vehicleNo !== "string" || !vehicleNo.trim()) {
      toast.error("Please enter vehicle number");
      return;
    }
    if (!selectedRC) {
      toast.error("Please select an RC type");
      return;
    }

    setIsVerifying(true);
    console.group(`🔍 [VERIFY] VRN: ${vehicleNo} | Type: ${selectedRC}`);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/services/rc/rc-full-verification-preview`,
        { id_number: vehicleNo, rc_type: selectedRC },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log(
        "📦 [VERIFY] Raw Response:",
        JSON.stringify(response.data, null, 2),
      );

      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to fetch RC data");
      }

      const rawData = response.data.data;

      // ✅ Resolve APIs used (array from new backend, string from old)
      const apisUsed = resolveApisUsed(response.data);
      console.log(
        `✅ [VERIFY] APIs contributed: ${formatApisUsedLabel(apisUsed)}`,
      );

      const fetchedData = normalizeRcData(rawData, vehicleNo);
      setApisUsedForCurrentData(apisUsed);

      setVerificationData({
        father_name: safeString(fetchedData?.father_name, "father_name"),
        vehicle_chasi_number: safeString(
          fetchedData?.vehicle_chasi_number,
          "vehicle_chasi_number",
        ),
        vehicle_engine_number: safeString(
          fetchedData?.vehicle_engine_number,
          "vehicle_engine_number",
        ),
      });

      setRcData(fetchedData);
      setIsSubmitEnabled(true);

      if (selectedRC === "Authorized_Rc") {
        setIncludeSignature(true);
        setIncludeChip(false);
      } else {
        setIncludeSignature(false);
        setIncludeChip(false);
      }

      setShowVerificationModal(true);
      toast.success(
        `Data fetched via ${formatApisUsedLabel(apisUsed)}. Review and proceed to pay.`,
      );
    } catch (err) {
      console.error("❌ [VERIFY] Error:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to fetch verification data. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsVerifying(false);
      console.groupEnd();
    }
  };

  // =========================================================================
  // SUBMIT VERIFICATION (with payment)
  // =========================================================================
  const handleSubmitVerification = async (isEditMode) => {
    if (isEditMode) {
      if (
        !verificationData.father_name.trim() ||
        !verificationData.vehicle_chasi_number.trim() ||
        !verificationData.vehicle_engine_number.trim()
      ) {
        toast.error("Please fill in all verification fields");
        return;
      }
    }

    setIsProcessingPayment(true);
    console.group(`🔍 [SUBMIT] VRN: ${vehicleNo} | Edit: ${isEditMode}`);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/services/rc/rc-full-verification`,
        { id_number: vehicleNo, rc_type: selectedRC, is_edited: isEditMode },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log(
        "📦 [SUBMIT] Raw Response:",
        JSON.stringify(response.data, null, 2),
      );

      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to verify RC data");
      }

      const rawData = response.data.data;

      // ✅ Resolve APIs used
      const apisUsed = resolveApisUsed(response.data);
      const apisLabel = formatApisUsedLabel(apisUsed);
      console.log(`✅ [SUBMIT] APIs contributed: ${apisLabel}`);

      let normalizedData = normalizeRcData(rawData, vehicleNo);
      setApisUsedForCurrentData(apisUsed);

      if (isEditMode) {
        normalizedData = {
          ...normalizedData,
          father_name: verificationData.father_name.toUpperCase(),
          vehicle_chasi_number:
            verificationData.vehicle_chasi_number.toUpperCase(),
          vehicle_engine_number:
            verificationData.vehicle_engine_number.toUpperCase(),
        };
        console.log("✏️ [SUBMIT] Applied edit mode overrides");
      }

      setRcData(normalizedData);
      setShowVerificationModal(false);
      setShowCard(true);
      setSubmitted(true);
      setShowEditFields(false);

      const newEntry = normalizeHistoryItem(
        {
          date: new Date().toLocaleString(),
          vehicleNo,
          documentType: selectedRC,
          name: normalizedData?.owner_name || " ",
          details: normalizedData?.maker_model || " ",
          status: "Success",
        },
        "new-entry",
      );
      setRcHistory((prev) => [newEntry, ...prev]);

      const breakdown = response.data.priceBreakdown;
      if (breakdown) {
        toast.success(
          `RC verified 🎉 Total: ₹${breakdown.total} | Source: ${apisLabel}`,
        );
      } else {
        toast.success(`RC downloaded successfully via ${apisLabel}!`);
      }
    } catch (err) {
      console.error("❌ [SUBMIT] Error:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to process request. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsProcessingPayment(false);
      console.groupEnd();
    }
  };

  // =========================================================================
  // DIRECT DOWNLOAD (confirmation modal path)
  // =========================================================================
  const proceedWithApiCall = async () => {
    setIsLoading(true);
    try {
      if (!rcData) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/services/rc/rc-full-verification`,
          { id_number: vehicleNo, rc_type: selectedRC, is_edited: false },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        if (response.data.success === false) {
          throw new Error(response.data.message || "Failed to fetch RC data");
        }
        const normalized = normalizeRcData(response.data.data, vehicleNo);
        setRcData(normalized);

        const apisUsed = resolveApisUsed(response.data);
        setApisUsedForCurrentData(apisUsed);
      }

      setShowCard(true);
      setSubmitted(true);

      const newEntry = normalizeHistoryItem(
        {
          date: new Date().toLocaleString(),
          vehicleNo,
          documentType: selectedRC,
          name: rcData?.owner_name || " ",
          details: rcData?.maker_model || " ",
          status: "Success",
        },
        "proceed-entry",
      );
      setRcHistory((prev) => [newEntry, ...prev]);

      toast.success("RC data fetched successfully");
      setShowConfirmationModal(false);
    } catch (err) {
      console.error("❌ [FRONTEND] Error in direct download:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to fetch RC data. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================================
  // DOWNLOAD IMAGE
  // =========================================================================
  const handleDownloadImage = async () => {
    if (!printRef.current) {
      toast.error("No document to download");
      return;
    }
    try {
      const cardElement = printRef.current;
      const canvas = await html2canvas(cardElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const link = document.createElement("a");

      let downloadSuffix = "no-sig";
      if (includeSignature && selectedRC === "Authorized_Rc") {
        downloadSuffix = `sig${getSignatureNumber(downloadCount)}`;
      } else if (includeChip && selectedRC === "smartrc") {
        downloadSuffix = "chip-enabled";
      }

      // ✅ Use formatted label from apisUsed array
      const apisLabel = formatApisUsedLabel(apisUsedForCurrentData).replace(
        / \+ /g,
        "-",
      );
      link.download = `RC-${vehicleNo}-${apisLabel}-${downloadSuffix}.jpg`;
      link.href = imgData;
      link.click();

      if (includeSignature && selectedRC === "Authorized_Rc") {
        setDownloadCount((prev) => prev + 1);
        toast.success(
          `Image downloaded with Signature #${getSignatureNumber(downloadCount)} (${apisLabel})`,
        );
      } else if (includeChip && selectedRC === "smartrc") {
        toast.success(`Image downloaded with Security Chip (${apisLabel})`);
      } else {
        toast.success(`Image downloaded successfully (${apisLabel})`);
      }
    } catch (err) {
      console.error("❌ [FRONTEND] Error generating image:", err);
      toast.error("Failed to generate image. Please try again.");
    }
  };

  // =========================================================================
  // DOWNLOAD PDF
  // =========================================================================
  const handlePrint = async () => {
    if (
      !rcData ||
      (selectedRC !== "smartrc" && selectedRC !== "Authorized_Rc")
    ) {
      if (!printRef.current) {
        toast.error("No document to print");
        return;
      }
      try {
        const cardElement = printRef.current;
        const canvas = await html2canvas(cardElement, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("portrait", "pt", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
        const scaledWidth = imgWidth * ratio;
        const scaledHeight = imgHeight * ratio;
        const marginX = (pageWidth - scaledWidth) / 2;
        const marginY = 30;
        pdf.addImage(
          imgData,
          "PNG",
          marginX,
          marginY,
          scaledWidth,
          scaledHeight,
        );
        pdf.save(`RC-${vehicleNo}.pdf`);
        if (includeSignature && selectedRC === "Authorized_Rc") {
          setDownloadCount((prev) => prev + 1);
        }
        toast.success(`PDF downloaded successfully`);
      } catch (err) {
        console.error("❌ [FRONTEND] Error generating PDF:", err);
        toast.error("Failed to generate PDF. Please try again.");
      }
      return;
    }

    const data = rcData;
    try {
      const currentSignatureUrl =
        includeSignature && selectedRC === "Authorized_Rc"
          ? getSignatureUrl(downloadCount)
          : null;
      const currentChipUrl =
        (includeChip && selectedRC === "Authorized_Rc") ||
        (includeChip && selectedRC === "smartrc")
          ? CLOUDINARY_IMAGES.chip
          : null;

      const imagesToLoad = {
        front: getBase64(Front),
        back: getBase64(Back),
        logo: getBase64(logo),
        chip: currentChipUrl ? getBase64(currentChipUrl) : null,
        signature: currentSignatureUrl ? getBase64(currentSignatureUrl) : null,
      };

      const loadedImagesObj = {};
      await Promise.all(
        Object.entries(imagesToLoad).map(async ([key, promise]) => {
          if (promise) loadedImagesObj[key] = await promise;
        }),
      );

      const frontImg = loadedImagesObj.front;
      const backImg = loadedImagesObj.back;
      const logoImg = loadedImagesObj.logo;
      const chipcard = loadedImagesObj.chip || null;
      const signatureImg = loadedImagesObj.signature || null;

      const stateCode =
        safeString(data?.rc_number, "rc_number")
          .trim()
          .slice(0, 2)
          .toUpperCase() || "";
      const stateName = stateCodesMap[stateCode] || "";

      const qrLines = [
        `State Name: ${safeString(data?.state_name || stateName, "state_name")}`,
        `Office Name: ${safeString(data?.office_name, "office_name")}`,
        `Vehicle Registration Number: ${safeString(data?.rc_number, "rc_number")}`,
        `Chassis Number: ${safeString(data?.vehicle_chasi_number, "vehicle_chasi_number")}`,
        `Engine Number: ${safeString(data?.vehicle_engine_number, "vehicle_engine_number")}`,
        `Seating Capacity: ${safeString(data?.seating_capacity || data?.seat_capacity, "seating_capacity")}`,
        `Standing Capacity: ${safeString(data?.standing_capacity, "standing_capacity")}`,
        `Sleeper Capacity: ${safeString(data?.sleeper_capacity, "sleeper_capacity")}`,
        `Owner Name: ${safeString(data?.owner_name, "owner_name")}`,
        `Laden Weight: ${safeString(data?.vehicle_gross_weight, "vehicle_gross_weight")}`,
        `Unladen Weight: ${safeString(data?.vehicle_unladen_weight || data?.unladen_weight, "unladen_weight")}`,
        `Father's Name: ${safeString(data?.father_name, "father_name")}`,
        `Registration From Date: ${safeString(data?.registration_date, "registration_date")}`,
        `Registration Upto Date: ${safeString(data?.fit_up_to, "fit_up_to")}`,
        `Address: ${safeString(data?.present_address, "present_address")}`,
      ];
      const qrText = qrLines.join(" | ");
      const qrDataUrl = await QRCodeLib.toDataURL(qrText, {
        width: 200,
        margin: 0,
        color: { dark: "#000000", light: "#0000" },
      });

      const maxAxleLoad = 10200;
      const grossWeight =
        parseInt(
          safeString(data?.vehicle_gross_weight, "vehicle_gross_weight"),
          10,
        ) || 0;
      const noOfAxle = Math.ceil(grossWeight / maxAxleLoad);

      const createCircleImage = async (text, bgColor) => {
        const canvas = document.createElement("canvas");
        canvas.width = 40;
        canvas.height = 40;
        const ctx = canvas.getContext("2d");
        ctx.beginPath();
        ctx.arc(20, 20, 18, 0, 2 * Math.PI);
        ctx.fillStyle = bgColor;
        ctx.fill();
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = "#000000";
        ctx.font = "bold 20px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, 20, 20);
        return canvas.toDataURL("image/png");
      };

      const ntCircle = await createCircleImage("NT", "#7dd3fc");
      const stateCircle = await createCircleImage(stateCode, "#fb923c");
      const ownerCircle = await createCircleImage(
        safeString(data?.owner_number || "1", "owner_number"),
        "#ffffff",
      );

      const createRotatedTextImage = (
        text,
        font = "Arial",
        fontSize = 9,
        padding = 8,
      ) => {
        const scale = window.devicePixelRatio
          ? Math.max(2, window.devicePixelRatio)
          : 2;
        const measureCanvas = document.createElement("canvas");
        const mctx = measureCanvas.getContext("2d");
        mctx.font = `${fontSize}px ${font}`;
        const metrics = mctx.measureText(text);
        const textWidth = Math.ceil(metrics.width);
        const textHeight = Math.ceil(fontSize);
        const cssWidth = textHeight + padding * 2;
        const cssHeight = textWidth + padding * 2;
        const canvas = document.createElement("canvas");
        canvas.width = cssWidth * scale;
        canvas.height = cssHeight * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);
        ctx.translate(cssWidth / 2, cssHeight / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.font = `${fontSize}px ${font}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#000";
        ctx.fillText(text, 0, 0);
        return canvas.toDataURL("image/png");
      };

      const cardIssueImg = createRotatedTextImage(
        `Card Issue Date: (${safeString(data?.permit_issue_date, "permit_issue_date") || "2025-07-25"})`,
      );
      const form23AImg = createRotatedTextImage("Form 23A");

      const docDefinition = {
        pageSize: { width: 585, height: 210 },
        pageOrientation: "landscape",
        pageMargins: [20, 20, 20, 20],
        content: [
          {
            columns: [
              {
                width: 265,
                stack: [
                  {
                    image: frontImg,
                    width: 265,
                    height: 167,
                    absolutePosition: { x: 15, y: 15 },
                  },
                  {
                    image: logoImg,
                    width: 21,
                    height: 38,
                    absolutePosition: { x: 20, y: 20 },
                  },
                  ...(chipcard
                    ? [
                        {
                          image: chipcard,
                          width: 55,
                          height: 37,
                          absolutePosition: { x: 20, y: 87 },
                        },
                      ]
                    : []),
                  {
                    stack: [
                      {
                        text: "Indian Union Vehicle Registration Certificate",
                        bold: true,
                        fontSize: 7.5,
                        lineHeight: 1,
                      },
                      {
                        text: `Issued by Government of ${stateName}`,
                        bold: true,
                        fontSize: 7.5,
                        lineHeight: 1,
                        margin: [0, 1, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 65, y: 25 },
                  },
                  {
                    columns: [
                      { image: ntCircle, width: 15, height: 15 },
                      {
                        image: stateCircle,
                        width: 15,
                        height: 15,
                        margin: [2, 0, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 240, y: 25 },
                  },
                  {
                    columns: [
                      {
                        stack: [
                          {
                            text: "Regn No",
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                          },
                          {
                            text: safeString(data?.rc_number, "rc_number"),
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                            margin: [0, 1, 0, 0],
                          },
                        ],
                        width: 50,
                      },
                      {
                        stack: [
                          {
                            text: "Date of Regn.",
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                          },
                          {
                            text: safeString(
                              data?.registration_date,
                              "registration_date",
                            ),
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                            margin: [0, 1, 0, 0],
                          },
                        ],
                        width: 55,
                      },
                      {
                        stack: [
                          {
                            text: "Regn. Validity",
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                          },
                          {
                            text: safeString(data?.fit_up_to, "fit_up_to"),
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                            margin: [0, 1, 0, 0],
                          },
                        ],
                        width: 50,
                      },
                      {
                        stack: [
                          {
                            text: "Owner",
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                          },
                          {
                            text: "Serial",
                            fontSize: 6,
                            bold: true,
                            lineHeight: 1,
                            margin: [0, 1, 0, 0],
                          },
                        ],
                        width: 30,
                      },
                      {
                        image: ownerCircle,
                        width: 15,
                        height: 15,
                        margin: [0, 0, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 75, y: 50 },
                  },
                  {
                    stack: [
                      {
                        text: "Chassis No",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(
                          data?.vehicle_chasi_number,
                          "vehicle_chasi_number",
                        ),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 0.5, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 75, y: 66 },
                  },
                  {
                    stack: [
                      {
                        text: "Engine/Motor No",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(
                          data?.vehicle_engine_number,
                          "vehicle_engine_number",
                        ),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 0.5, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 75, y: 82 },
                  },
                  {
                    stack: [
                      {
                        text: "Owner Name",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(data?.owner_name, "owner_name"),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 0.5, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 75, y: 98 },
                  },
                  {
                    stack: [
                      {
                        text: "Son/Wife/Daughter of (In case of Individual Owner)",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(data?.father_name, "father_name"),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 0.5, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 75, y: 114 },
                  },
                  {
                    stack: [
                      {
                        text: "Ownership",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: "INDIVIDUAL",
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 0.5, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 75, y: 130 },
                  },
                  {
                    absolutePosition: { x: 75, y: 146 },
                    stack: [
                      {
                        text: "Address",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        table: {
                          widths: [180],
                          body: [
                            [
                              {
                                text: safeString(
                                  data?.present_address,
                                  "present_address",
                                ),
                                fontSize: 5.5,
                                lineHeight: 1.15,
                                border: [false, false, false, false],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: () => 0,
                          vLineWidth: () => 0,
                          paddingLeft: () => 0,
                          paddingRight: () => 2,
                          paddingTop: () => 0,
                          paddingBottom: () => 0,
                        },
                      },
                    ],
                  },
                  {
                    stack: [
                      { text: "Fuel", fontSize: 6, bold: true, lineHeight: 1 },
                      {
                        text: safeString(data?.fuel_type, "fuel_type"),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 0.5, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 20, y: 147 },
                  },
                  {
                    stack: [
                      {
                        text: "Emission Norms",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(data?.norms_type, "norms_type"),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 0.5, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 20, y: 163 },
                  },
                  {
                    image: cardIssueImg,
                    width: 18,
                    height: 90,
                    absolutePosition: { x: 260, y: 75 },
                  },
                ],
              },
              {
                width: 265,
                stack: [
                  {
                    image: backImg,
                    width: 265,
                    height: 167,
                    absolutePosition: { x: 305, y: 15 },
                  },
                  {
                    columns: [
                      { image: ntCircle, width: 15, height: 15 },
                      {
                        image: stateCircle,
                        width: 15,
                        height: 15,
                        margin: [2, 0, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 310, y: 23 },
                  },
                  {
                    text: [
                      { text: "Vehicle Class: ", bold: true, fontSize: 7 },
                      {
                        text: safeString(
                          data?.vehicle_category_description,
                          "vehicle_category_description",
                        ),
                        fontSize: 7,
                      },
                    ],
                    absolutePosition: { x: 350, y: 28 },
                  },
                  {
                    stack: [
                      {
                        text: "Regn. No",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(data?.rc_number, "rc_number"),
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                        margin: [0, 1, 0, 3],
                      },
                    ],
                    absolutePosition: { x: 310, y: 48 },
                  },
                  {
                    image: qrDataUrl,
                    width: 70,
                    height: 70,
                    absolutePosition: { x: 310, y: 65 },
                  },
                  {
                    stack: [
                      {
                        text: "Month-Year of Mfg.",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(
                          data?.manufacturing_date,
                          "manufacturing_date",
                        ),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 1, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 310, y: 140 },
                  },
                  {
                    text: [
                      { text: "No. of Cylinders ", bold: true, fontSize: 6 },
                      {
                        text: safeString(data?.no_cylinders, "no_cylinders"),
                        fontSize: 6,
                      },
                    ],
                    absolutePosition: { x: 310, y: 160 },
                  },
                  {
                    text: [
                      { text: "No of Axle ", bold: true, fontSize: 6 },
                      { text: noOfAxle.toString(), fontSize: 6 },
                    ],
                    absolutePosition: { x: 310, y: 169 },
                  },
                  {
                    stack: [
                      {
                        text: "Maker's Name:",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(
                          data?.maker_description,
                          "maker_description",
                        ),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 1, 0, 2],
                      },
                    ],
                    absolutePosition: { x: 390, y: 48 },
                  },
                  {
                    stack: [
                      {
                        text: "Model Name:",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(data?.maker_model, "maker_model"),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 1, 0, 2],
                      },
                    ],
                    absolutePosition: { x: 390, y: 63 },
                  },
                  {
                    columns: [
                      {
                        width: 60,
                        stack: [
                          {
                            text: "Color:",
                            bold: true,
                            fontSize: 6,
                            lineHeight: 1,
                          },
                          {
                            text: safeString(data?.color, "color"),
                            fontSize: 6,
                            lineHeight: 1,
                          },
                        ],
                      },
                      {
                        width: 80,
                        stack: [
                          {
                            text: "/ Body Type:",
                            bold: true,
                            fontSize: 6,
                            lineHeight: 1,
                          },
                          {
                            text:
                              "/" + safeString(data?.body_type, "body_type"),
                            fontSize: 6,
                            lineHeight: 1,
                          },
                        ],
                      },
                    ],
                    absolutePosition: { x: 390, y: 78 },
                  },
                  {
                    columns: [
                      {
                        width: 40,
                        stack: [
                          {
                            text: "Seating (in all) ",
                            bold: true,
                            fontSize: 6,
                          },
                          {
                            text: safeString(
                              data?.seat_capacity,
                              "seat_capacity",
                            ),
                            fontSize: 6,
                          },
                        ],
                      },
                      {
                        width: 40,
                        stack: [
                          { text: "/ Standing ", bold: true, fontSize: 6 },
                          {
                            text:
                              "/" +
                              safeString(
                                data?.standing_capacity,
                                "standing_capacity",
                              ),
                            fontSize: 6,
                          },
                        ],
                      },
                      {
                        width: 60,
                        stack: [
                          {
                            text: "/ Sleeper capacity",
                            bold: true,
                            fontSize: 6,
                          },
                          {
                            text:
                              "/" +
                              safeString(
                                data?.sleeper_capacity,
                                "sleeper_capacity",
                              ),
                            fontSize: 6,
                          },
                        ],
                      },
                    ],
                    absolutePosition: { x: 390, y: 93 },
                  },
                  {
                    columns: [
                      {
                        width: 30,
                        stack: [
                          { text: "Unladen:", bold: true, fontSize: 6 },
                          {
                            text: safeString(
                              data?.unladen_weight,
                              "unladen_weight",
                            ),
                            fontSize: 6,
                          },
                        ],
                      },
                      {
                        width: 30,
                        stack: [
                          { text: "/ Laden:", bold: true, fontSize: 6 },
                          {
                            text:
                              "/" +
                              safeString(
                                data?.vehicle_gross_weight,
                                "vehicle_gross_weight",
                              ),
                            fontSize: 6,
                          },
                        ],
                      },
                      {
                        width: 100,
                        stack: [
                          {
                            text: "/ Gross Combined Weight (kg)",
                            bold: true,
                            fontSize: 6,
                          },
                          {
                            text:
                              "/" +
                              safeString(
                                data?.gross_combination_weight,
                                "gross_combination_weight",
                              ),
                            fontSize: 6,
                          },
                        ],
                      },
                    ],
                    absolutePosition: { x: 390, y: 107 },
                  },
                  {
                    columns: [
                      {
                        width: 35,
                        stack: [
                          { text: "Cubic Cap.", bold: true, fontSize: 6 },
                          {
                            text: safeString(
                              data?.cubic_capacity,
                              "cubic_capacity",
                            ),
                            fontSize: 6,
                          },
                        ],
                      },
                      {
                        width: 70,
                        stack: [
                          {
                            text: "/ Horse Power (BHP/Kw) ",
                            bold: true,
                            fontSize: 6,
                          },
                          {
                            text:
                              "/" +
                              safeString(data?.horse_power, "horse_power"),
                            fontSize: 6,
                          },
                        ],
                      },
                      {
                        width: 60,
                        stack: [
                          {
                            text: "/ Wheel Base (mm) ",
                            bold: true,
                            fontSize: 6,
                          },
                          {
                            text:
                              "/" + safeString(data?.wheelbase, "wheelbase"),
                            fontSize: 6,
                          },
                        ],
                      },
                    ],
                    absolutePosition: { x: 390, y: 121 },
                  },
                  {
                    stack: [
                      {
                        text: "Financier",
                        fontSize: 6,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(data?.financer, "financer"),
                        fontSize: 6,
                        lineHeight: 1,
                        margin: [0, 1, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 390, y: 135 },
                  },
                  {
                    stack: [
                      {
                        text: "Registration Authority",
                        fontSize: 5.5,
                        bold: true,
                        lineHeight: 1,
                      },
                      {
                        text: safeString(data?.registered_at, "registered_at"),
                        fontSize: 5.5,
                        lineHeight: 1,
                        margin: [0, 1, 0, 0],
                      },
                    ],
                    absolutePosition: { x: 485, y: 163 },
                  },
                  ...(signatureImg
                    ? [
                        {
                          image: signatureImg,
                          width: 42,
                          height: 15,
                          absolutePosition: { x: 493, y: 148 },
                        },
                      ]
                    : []),
                  {
                    image: form23AImg,
                    width: 15,
                    height: 50,
                    absolutePosition: { x: 555, y: 85 },
                  },
                ],
              },
            ],
          },
        ],
      };

      pdfMake
        .createPdf(docDefinition)
        .download(`RC-${safeString(data?.rc_number, "rc_number") || "RC"}.pdf`);

      if (includeSignature && selectedRC === "Authorized_Rc") {
        setDownloadCount((prev) => prev + 1);
      }
      toast.success(`PDF downloaded successfully`);
    } catch (error) {
      console.error("❌ [FRONTEND] Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  const paginatedData = Array.isArray(rcHistory)
    ? rcHistory.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      )
    : [];

  const totalPages = Math.ceil(
    Array.isArray(rcHistory) ? rcHistory.length / itemsPerPage : 0,
  );
  const handlePrev = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const applyFilters = () => {
    let filteredData = [...allRcHistory];
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredData = filteredData.filter((item) =>
        safeString(item.vehicleNo, "vehicleNo")
          .toLowerCase()
          .includes(searchTerm),
      );
    }
    if (filters.status) {
      filteredData = filteredData.filter(
        (item) =>
          safeString(item.status, "status").toLowerCase() ===
          filters.status.toLowerCase(),
      );
    }
    if (filters.fromDate || filters.toDate) {
      const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
      const toDate = filters.toDate ? new Date(filters.toDate) : null;
      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item.date);
        let matches = true;
        if (fromDate && itemDate < fromDate) matches = false;
        if (toDate && itemDate > toDate) matches = false;
        return matches;
      });
    }
    setRcHistory(filteredData);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: "", status: "", fromDate: "", toDate: "" });
    setRcHistory(allRcHistory);
    setCurrentPage(1);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, allRcHistory]);

  const visiblePages = useMemo(() => {
    const delta = 2;
    const pages = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  const handleConfirmationModal = () => {
    const basePrice = rcPrices[selectedRC] || 0;
    return (
      showConfirmationModal &&
      !showCard && (
        <PortalModal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4">Confirm Payment</h3>
              <div className="mb-4">
                <div className="flex justify-between mb-2">
                  <span>RC Verification ({selectedRC}):</span>
                  <span className="font-semibold">₹{basePrice}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Amount:</span>
                  <span className="text-red-600">₹{basePrice}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Do you want to proceed with the payment?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    setRcData(null);
                  }}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={proceedWithApiCall}
                  disabled={isLoading}
                  className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {isLoading ? "Processing..." : "Confirm & Pay"}
                </button>
              </div>
            </div>
          </div>
        </PortalModal>
      )
    );
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <>
      <div className="py-4 w-full mx-auto print:bg-white">
        {handleConfirmationModal()}

        {showCard && rcData && (
          <PortalModal>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 print:static">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto relative p-6 print:static print:block print:shadow-none print:p-0 print:max-h-full print:overflow-visible">
                <div className="my-4 flex flex-wrap justify-between items-center gap-2">
                  <button
                    onClick={() => {
                      setShowCard(false);
                      setShowConfirmationModal(false);
                    }}
                    className="bg-gray-300 px-3 py-2 rounded hover:bg-gray-400 text-sm md:text-md"
                  >
                    Close
                  </button>
                  {/* ✅ Show which APIs contributed data
                  {apisUsedForCurrentData.length > 0 && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      Source: {formatApisUsedLabel(apisUsedForCurrentData)}
                    </span>
                  )} */}
                  <button
                    onClick={handlePrint}
                    className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-xs md:text-sm"
                  >
                    Download PDF
                  </button>
                </div>
                <div ref={printRef}>
                  {selectedRC === "parivaanrc" && (
                    <ParivahanRCView
                      data={rcData}
                      includeSignature={false}
                      includeChip={false}
                      signatureUrl={null}
                      downloadCount={downloadCount}
                    />
                  )}
                  {selectedRC === "Authorized_Rc" && (
                    <AuthorizedRCView
                      data={rcData}
                      includeSignature={includeSignature}
                      includeChip={includeChip}
                      signatureUrl={getSignatureUrl(downloadCount)}
                      downloadCount={downloadCount}
                    />
                  )}
                  {selectedRC === "digilockerrc" && (
                    <DigiLockerRCView
                      data={rcData}
                      includeSignature={false}
                      includeChip={false}
                      signatureUrl={null}
                      downloadCount={downloadCount}
                    />
                  )}
                  {selectedRC === "smartrc" && (
                    <SmartRCView
                      data={rcData}
                      includeSignature={false}
                      includeChip={includeChip}
                      signatureUrl={includeChip ? CLOUDINARY_IMAGES.chip : null}
                      downloadCount={downloadCount}
                    />
                  )}
                </div>
              </div>
            </div>
          </PortalModal>
        )}

        {!showCard && (
          <>
            <div className="w-full mx-auto md:py-4">
              <div className="bg-blue-100 p-2">
                <h2 className="text-center text-xl font-semibold text-blue-700">
                  Registration Certificate
                </h2>
              </div>

              <div className="flex flex-col md:justify-between gap-4 md:flex-row md:items-center pt-6">
                <input
                  type="text"
                  placeholder="Enter Vehicle No"
                  value={vehicleNo}
                  onChange={(e) => setVehicleNo(e.target.value)}
                  className="w-full md:w-1/2 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {isAccessLoading ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
                    <p className="mt-2 text-sm text-gray-600">
                      Loading access options...
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:flex w-full gap-3">
                    {[
                      { key: "parivaanrc", label: "Privahan RC" },
                      { key: "Authorized_Rc", label: "Authorized RC" },
                      { key: "smartrc", label: "Smart RC" },
                      { key: "digilockerrc", label: "Digilocker RC" },
                    ].map(({ key, label }) => (
                      <div className="w-full md:w-1/4" key={key}>
                        <label
                          className={`flex w-full items-center gap-1 ${!rcAccess[key] ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <input
                            type="radio"
                            name="rc"
                            value={key}
                            checked={selectedRC === key}
                            disabled={!rcAccess[key]}
                            onChange={() => setSelectedRC(key)}
                          />
                          <span className="text-sm md:text-base">
                            {label} <br />
                            <span className="text-red-400 text-xs md:text-sm">
                              ₹ {rcPrices[key]}
                            </span>
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-start mt-4">
                <button
                  onClick={handleVerify}
                  disabled={isVerifying || !selectedRC}
                  className={`bg-indigo-600 text-white py-2 px-6 rounded hover:bg-indigo-700 ${isVerifying ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  {isVerifying ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    "VERIFY"
                  )}
                </button>
              </div>
            </div>

            {showVerificationModal && (
              <PortalModal>
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4 md:mb-6 flex-wrap gap-2">
                      <h3 className="text-base md:text-lg font-semibold">
                        VRN Verify Result for ({vehicleNo})
                      </h3>
                      {/* ✅ Show data sources in modal header */}
                      {/* {apisUsedForCurrentData.length > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-50 border border-blue-200 px-2 py-1 rounded">
                          Data from:{" "}
                          {formatApisUsedLabel(apisUsedForCurrentData)}
                        </span>
                      )} */}
                    </div>

                    {(selectedRC === "Authorized_Rc" ||
                      selectedRC === "smartrc") && (
                      <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <label
                          htmlFor="includeChipEdit"
                          className="flex items-start gap-3 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            id="includeChipEdit"
                            checked={includeChip}
                            onChange={(e) => setIncludeChip(e.target.checked)}
                            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-gray-800">
                              Include Security Chip
                            </p>
                            <p className="text-xs text-gray-500 leading-snug">
                              Adds an RFID security chip design to the RC card
                              for enhanced authenticity
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    <div className="bg-gray-50 p-4 rounded border border-gray-200 mb-4 md:mb-6">
                      <h4 className="font-semibold text-sm mb-3 md:mb-4">
                        Payment Summary
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>RC Download ({selectedRC}):</span>
                          <span className="font-semibold">
                            ₹{rcPrices[selectedRC] || 0}
                          </span>
                        </div>
                        <hr className="my-2" />
                        <div className="flex justify-between text-base md:text-lg font-bold">
                          <span>Total Amount:</span>
                          <span className="text-red-600">
                            ₹{rcPrices[selectedRC] || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-2 mb-4">
                      <button
                        onClick={() => handleSubmitVerification(false)}
                        disabled={isProcessingPayment}
                        className="flex-1 px-4 py-3 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
                      >
                        {isProcessingPayment
                          ? "Processing..."
                          : `Download (₹${rcPrices[selectedRC] || 0})`}
                      </button>
                      {(selectedRC === "smartrc" ||
                        selectedRC === "Authorized_Rc") && (
                        <button
                          onClick={() => setShowEditFields(!showEditFields)}
                          className="flex-1 px-4 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                          {showEditFields ? "Hide Edit" : "Edit RC Details"}
                        </button>
                      )}
                    </div>

                    {showEditFields &&
                      (selectedRC === "smartrc" ||
                        selectedRC === "Authorized_Rc") && (
                        <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200">
                          <h4 className="font-semibold text-sm mb-4">
                            Edit RC Details
                          </h4>
                          <div className="space-y-4">
                            {[
                              {
                                label: "Son/Wife/Daughter of",
                                key: "father_name",
                                placeholder: "Enter name",
                              },
                              {
                                label: "Chassis Number",
                                key: "vehicle_chasi_number",
                                placeholder: "Enter chassis number",
                              },
                              {
                                label: "Engine Number",
                                key: "vehicle_engine_number",
                                placeholder: "Enter engine number",
                              },
                            ].map(({ label, key, placeholder }) => (
                              <div key={key}>
                                <label className="block text-sm font-medium mb-1">
                                  {label}
                                </label>
                                <input
                                  type="text"
                                  value={verificationData[key]}
                                  onChange={(e) =>
                                    setVerificationData({
                                      ...verificationData,
                                      [key]: e.target.value,
                                    })
                                  }
                                  placeholder={placeholder}
                                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                              </div>
                            ))}
                            <button
                              onClick={() => handleSubmitVerification(true)}
                              disabled={isProcessingPayment}
                              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                            >
                              {isProcessingPayment
                                ? "Applying Changes..."
                                : "Apply Changes & Download"}
                            </button>
                          </div>
                        </div>
                      )}

                    <button
                      onClick={() => {
                        setShowVerificationModal(false);
                        setShowEditFields(false);
                        setVerificationData({
                          father_name: "",
                          vehicle_chasi_number: "",
                          vehicle_engine_number: "",
                        });
                      }}
                      className="w-full px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </PortalModal>
            )}

            {/* History Table */}
            <div className="w-full mt-6">
              <div className="bg-white p-4 shadow-md rounded-lg mb-4">
                <h3 className="text-lg font-semibold mb-4">
                  Current RC Results
                </h3>

                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h4 className="text-md font-medium mb-3">
                    Filter Current Results
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Search
                      </label>
                      <input
                        type="text"
                        placeholder="Search vehicle number"
                        value={filters.search}
                        onChange={(e) =>
                          setFilters({ ...filters, search: e.target.value })
                        }
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Status
                      </label>
                      <select
                        value={filters.status}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        className="w-full border rounded px-3 py-2 text-sm"
                      >
                        <option value="">All Status</option>
                        <option value="Success">Success</option>
                        <option value="Failed">Failed</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={filters.fromDate}
                        onChange={(e) =>
                          setFilters({ ...filters, fromDate: e.target.value })
                        }
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={filters.toDate}
                        onChange={(e) =>
                          setFilters({ ...filters, toDate: e.target.value })
                        }
                        className="w-full border rounded px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                    >
                      Clear
                    </button>
                    <button
                      onClick={applyFilters}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                      Apply
                    </button>
                  </div>
                </div>

                {rcHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No records found matching your filters
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                              Sl No
                            </th>
                            <th className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                              Vehicle No.
                            </th>
                            <th className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                              Type
                            </th>
                            <th className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                              Date & Time
                            </th>
                            <th className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedData.map((item, index) => {
                            const safeItem = {
                              vehicleNo: safeString(
                                item.vehicleNo,
                                `row[${index}].vehicleNo`,
                              ),
                              documentType: safeString(
                                item.documentType,
                                `row[${index}].documentType`,
                              ),
                              date: item.date,
                              status: safeString(
                                item.status,
                                `row[${index}].status`,
                              ),
                            };

                            return (
                              <tr
                                key={index}
                                className="text-center hover:bg-gray-50"
                              >
                                <td className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                                  {(currentPage - 1) * itemsPerPage + index + 1}
                                </td>
                                <td className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                                  {safeItem.vehicleNo}
                                </td>
                                <td className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                                  {safeItem.documentType}
                                </td>
                                <td className="px-2 md:px-4 py-2 border text-xs md:text-sm">
                                  {new Date(safeItem.date).toLocaleString()}
                                </td>
                                <td className="px-2 md:px-4 py-2 border">
                                  <span
                                    className={`px-2 py-1 rounded text-white text-xs ${
                                      safeItem.status.toLowerCase() ===
                                      "success"
                                        ? "bg-green-500"
                                        : "bg-red-500"
                                    }`}
                                  >
                                    {safeItem.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-4">
                      <span className="text-xs md:text-sm text-gray-500">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, rcHistory.length)}{" "}
                        of {rcHistory.length} entries
                      </span>
                      <div className="flex gap-2 flex-wrap justify-center">
                        <button
                          onClick={handlePrev}
                          disabled={currentPage === 1}
                          className="px-3 py-1 border rounded text-xs md:text-sm disabled:opacity-50"
                        >
                          Prev
                        </button>
                        {visiblePages[0] > 1 && (
                          <span className="px-2 text-xs md:text-sm">...</span>
                        )}
                        {visiblePages.map((p) => (
                          <button
                            key={p}
                            onClick={() => setCurrentPage(p)}
                            className={`px-3 py-1 border rounded text-xs md:text-sm ${currentPage === p ? "bg-blue-600 text-white" : ""}`}
                          >
                            {p}
                          </button>
                        ))}
                        {visiblePages[visiblePages.length - 1] < totalPages && (
                          <span className="px-2 text-xs md:text-sm">...</span>
                        )}
                        <button
                          onClick={handleNext}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 border rounded text-xs md:text-sm disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
